/**
 * CLOSING A WORK ORDER — and closing one short.
 *
 * A work order used to have no end. It reached VENDACK when the contractor
 * acknowledged it and stayed there for ever, whether the work finished last
 * week or was abandoned in March. 0156 gave it two ways out:
 *
 *   Close out      the work finished, everything is billed and paid
 *   Close short    the work stopped and will not resume — reason compulsory
 *
 * WHY THIS SCREEN SHOWS THE BLOCKERS RATHER THAN A GREY BUTTON
 *
 * fn_wo_close_blockers returns the reasons, one sentence per row, and both
 * the RPC and this card read the same function. So the list you see here is
 * word for word what the server would have refused with — they cannot drift
 * apart, because there is only one of them.
 *
 * A disabled button with no explanation is the commonest small cruelty in
 * business software. The man is holding a file he believes is finished; the
 * system knows exactly which payable is short by how much; and it says
 * nothing. This says it.
 *
 * WHY RETENTION IS NOT IN THAT LIST
 *
 * Board instruction, 20-09-2026. The defect liability period runs for a year
 * AFTER the work is complete, so waiting for the retention to go back before
 * closing would hold every finished contract open for a year. A closed order
 * carrying retention stays on the statutory register until the last rupee is
 * released — 0155 makes sure of that — and the audit trail on the closure
 * says how much is still held and why the order closed anyway.
 */
import { useState } from 'react'
import { Link } from 'react-router-dom'
import {
  AlertTriangle, Ban, CheckCircle2, CircleSlash, ExternalLink, Lock, PackageCheck,
  RotateCcw, ShieldAlert,
} from 'lucide-react'
import { useAuth } from '@/lib/auth'
import { dbMessage, useQuery } from '@/lib/data'
import { supabase } from '@/lib/supabase'
import { dateTime, rupees } from '@/lib/format'
import {
  Alert, Card, Field, Loading, Modal, Textarea, useToast,
} from '@/components/ui'

/** Mirrors may_close_wo(). The function is the enforcement; this is the door. */
const MAY_CLOSE = ['admin', 'cmd', 'project_head', 'procurement_head']

/**
 * Mirrors rpc_wo_reopen — CMD and admin only.
 *
 * Deliberately NARROWER than MAY_CLOSE. Closing an order is a Project Head
 * decision; unclosing one says that decision was wrong, and a man should not
 * be able to quietly reverse his own conclusion on a document the accounts
 * have already moved on from.
 */
const MAY_REOPEN = ['admin', 'cmd']

/** Mirrors rpc_wo_cancel — the same people who may close one. */
const MAY_CANCEL = ['admin', 'cmd', 'project_head', 'procurement_head']

/**
 * An order at one of these statuses is NOT YET A CONTRACT — the contractor has
 * not acknowledged it, and on most of them he has not even seen it.
 *
 * It cannot be closed, because there is nothing to close out; the only way to
 * end it is to cancel it. The card says so in those words rather than showing
 * a "Closing this order" heading above a blocker explaining that it cannot be
 * closed, which is what it did when 0172 first shipped.
 */
const NOT_YET_A_CONTRACT = ['DRAFT', 'SUBMITTED', 'RETURNED', 'REJECTED',
                            'APPROVED', 'VENDIN']

interface ClosureRow {
  wo_id: string; site_id: string; wo_status: string
  closed_at: string | null; closed_by: string | null; close_remarks: string | null
  short_closed_at: string | null; short_closed_by: string | null
  short_close_reason: string | null
  closed_by_name: string | null
  cancelled_at: string | null; cancelled_by: string | null
  cancel_reason: string | null; cancelled_by_name: string | null
  blocker_count: number
  first_blocker: string | null
  blockers: string[] | null
  short_close_blocker_count: number
  retention_still_held: number
}

export function WoCloseOut({ woId, onChanged }: { woId: string; onChanged: () => void }) {
  const { profile } = useAuth()
  const toast = useToast()
  const [asking, setAsking] = useState<'CLOSE' | 'SHORT' | 'REOPEN' | 'CANCEL' | null>(null)
  const [text, setText] = useState('')
  const [busy, setBusy] = useState(false)

  const { data: rows, loading, refresh } = useQuery<ClosureRow>(
    `wo_closure_${woId}`,
    () => supabase.from('v_wo_closure').select('*').eq('wo_id', woId).limit(1),
    [woId], { cacheable: false, idField: 'wo_id' })

  /*
    WHERE THE BLOCKER ACTUALLY IS — 0173.

    fn_wo_close_blockers returns sentences, and the two commonest of them name
    a document: "RA-3 is still draft. Finish it or withdraw it first." and
    "Measurement MB-… is still draft." Both were dead ends. The remedy existed
    from 22-09-2026, and the man reading the sentence still had to go and find
    the document by hand — which on an order with nine measurement pages is
    exactly the friction that makes people close orders short instead.

    Two small reads, and the sentence gets a door. They are fetched regardless
    of status so a closed order shows nothing (there is nothing in the chain),
    and are deliberately NOT a join on the view: v_wo_closure is doing
    arithmetic for the whole module and should not grow columns for a link.
  */
  const { data: openBills } = useQuery<any>(
    `wo_closure_bills_${woId}`,
    () => supabase.from('wo_ra_bills').select('id, ra_no, status')
      .eq('wo_id', woId).in('status', ['DRAFT', 'SUBMITTED', 'RETURNED']),
    [woId], { cacheable: false })

  const { data: openMbs } = useQuery<any>(
    `wo_closure_mbs_${woId}`,
    () => supabase.from('wo_measurements').select('id, status, measurement_date')
      .eq('wo_id', woId).in('status', ['DRAFT', 'SUBMITTED', 'RETURNED']),
    [woId], { cacheable: false })

  if (loading) return <Loading label="Checking what is outstanding…" />
  const c = rows[0]
  if (!c) return null

  const isClosed = c.wo_status === 'CLOSED'
  const isShort = c.wo_status === 'FORCED_CLOSE'
  const isCancelled = c.wo_status === 'CANCELLED'
  /*
    CANCELLING — 0172. An order raised by mistake, before anything happened
    under it. Deliberately NOT the same act as closing short: that records
    that work stopped, this records that the order should never have existed.
    rpc_wo_cancel refuses the moment anything has been measured, billed,
    varied, advanced or released.
  */
  const mayCancel = MAY_CANCEL.includes(profile?.role ?? '')
    && !['CLOSED', 'FORCED_CLOSE', 'CANCELLED'].includes(c.wo_status)
  const mayClose = MAY_CLOSE.includes(profile?.role ?? '')
  const blockers = c.blockers ?? []

  // The short close forgives exactly one blocker — the missing final bill —
  // and nothing else. Anything that stops a short close stops a close too.
  const shortBlocked = c.short_close_blocker_count > 0

  const act = async (kind: 'CLOSE' | 'SHORT' | 'REOPEN' | 'CANCEL') => {
    setBusy(true)
    const { error } =
      kind === 'CLOSE'
        ? await supabase.rpc('rpc_wo_close', { p_wo_id: woId, p_remarks: text.trim() || null })
        : kind === 'SHORT'
          ? await supabase.rpc('rpc_wo_short_close', { p_wo_id: woId, p_reason: text.trim() })
          : kind === 'REOPEN'
            ? await supabase.rpc('rpc_wo_reopen', { p_wo_id: woId, p_reason: text.trim() })
            : await supabase.rpc('rpc_wo_cancel', { p_wo_id: woId, p_reason: text.trim() })
    setBusy(false)
    if (error) {
      toast.error(dbMessage(error, 'That did not go through.'))
      return
    }
    setAsking(null); setText('')
    refresh(); onChanged()
    toast.success(kind === 'CLOSE' ? 'Order closed out.'
      : kind === 'SHORT' ? 'Order closed short.'
      : kind === 'REOPEN' ? 'Order reopened.' : 'Order cancelled.')
  }

  /* ------------------------------------------------------------- cancelled */
  if (isCancelled) {
    return (
      <Card title="Cancelled">
        <div className="flex items-start gap-3">
          <Ban className="mt-0.5 h-5 w-5 shrink-0 text-red-600" />
          <div className="min-w-0 flex-1 space-y-2">
            <p className="text-sm text-slate-700">
              This order was raised and then cancelled. Nothing had been measured, billed,
              varied or advanced against it.
            </p>
            {c.cancel_reason && (
              <blockquote className="border-l-2 border-red-300 pl-3 text-sm text-slate-700">
                {c.cancel_reason}
              </blockquote>
            )}
            <p className="text-xs text-slate-400">
              {c.cancelled_by_name ?? 'Somebody no longer on the system'} on{' '}
              {dateTime(c.cancelled_at)}
            </p>
            <p className="flex items-start gap-2 pt-1 text-xs text-slate-500">
              <Lock className="mt-0.5 h-3.5 w-3.5 shrink-0" />
              It stays on the record with its lines, its terms and every approval on it.
              Nothing can be raised against it.
            </p>
          </div>
        </div>
      </Card>
    )
  }

  /*
    ONE CANCEL MODAL, SHARED BY BOTH BRANCHES.

    The card renders differently for an order that is not yet a contract and
    for one that is, but the act is the same act and must read the same way in
    both. Defining it here rather than in each branch is what stops the two
    wordings drifting apart.
  */
  const cancelModal = (
    <Modal open={asking === 'CANCEL'} onClose={() => setAsking(null)}
           title="Cancel this work order"
           subtitle="It was raised by mistake and nothing has happened under it."
           footer={
             <>
               <button className="btn-secondary" onClick={() => setAsking(null)} disabled={busy}>
                 Keep it
               </button>
               <button className="btn-danger" disabled={busy || text.trim().length < 15}
                       onClick={() => void act('CANCEL')}>
                 {busy ? 'Cancelling…' : 'Cancel the order'}
               </button>
             </>
           }>
      <div className="space-y-3">
        <Alert tone="warning" title="Nothing is deleted">
          The order stays on the record — its lines, its terms and every approval on it.
          It is marked cancelled, and no measurement, bill, variation, advance or
          retention release can ever be raised against it.
        </Alert>
        <p className="text-sm text-slate-600">
          This is <strong>not</strong> the same as closing short. A short close says the
          work stopped part-way; this says the order should never have existed. If
          anything has already been measured or billed, the database will refuse this
          and tell you what is in the way.
        </p>
        <Field label="Why it is being cancelled" required
               hint="At least a sentence. It is the only thing that will explain to
                     anybody reading this order why it was raised and then abandoned.">
          <Textarea rows={4} value={text} onChange={(e) => setText(e.target.value)}
                    placeholder="Raised against the lump-sum schedule item by mistake and approved before it was spotted. Replaced by WO-… on the correct measured item. Never sent to the contractor." />
        </Field>
        {text.trim().length > 0 && text.trim().length < 15 && (
          <p className="text-xs text-amber-700">
            A few more words — {15 - text.trim().length} to go.
          </p>
        )}
      </div>
    </Modal>
  )

  /* ------------------------------------------------- not a contract yet ---- */
  if (NOT_YET_A_CONTRACT.includes(c.wo_status)) {
    if (!mayCancel) return null
    return (
      <>
        <Card title="Ending this order"
              subtitle="It has not been acknowledged by the contractor, so there is nothing to close — only something to cancel.">
          <div className="flex items-start gap-3">
            <Ban className="mt-0.5 h-5 w-5 shrink-0 text-slate-400" />
            <div className="flex-1 space-y-2">
              <p className="text-sm text-slate-600">
                An order raised by mistake is <strong>cancelled</strong>. It stays on the
                record with its lines, its terms and every approval on it, marked cancelled
                with the reason — and nothing can be raised against it afterwards.
              </p>
              <p className="text-xs text-slate-500">
                Closing out and closing short become available once the contractor has
                acknowledged the order and work has been billed against it. They say the
                work finished, or that it stopped; neither is true of an order that never
                started.
              </p>
              <button className="btn-danger btn-sm" disabled={busy}
                      onClick={() => { setText(''); setAsking('CANCEL') }}>
                <Ban className="h-4 w-4" /> Cancel the order
              </button>
            </div>
          </div>
        </Card>
        {cancelModal}
      </>
    )
  }

  /* ---------------------------------------------------------------- closed */
  if (isClosed || isShort) {
    return (
      <Card title={isShort ? 'Closed short' : 'Closed out'}>
        <div className="flex items-start gap-3">
          {isShort
            ? <CircleSlash className="mt-0.5 h-5 w-5 shrink-0 text-amber-600" />
            : <CheckCircle2 className="mt-0.5 h-5 w-5 shrink-0 text-emerald-600" />}
          <div className="min-w-0 flex-1 space-y-2">
            <p className="text-sm text-slate-700">
              {isShort
                ? 'The work stopped with the contract incomplete. The balance will not be executed.'
                : 'The work finished, everything was billed, certified and paid.'}
            </p>
            {isShort && c.short_close_reason && (
              <blockquote className="border-l-2 border-amber-300 pl-3 text-sm text-slate-700">
                {c.short_close_reason}
              </blockquote>
            )}
            {!isShort && c.close_remarks && (
              <p className="text-sm text-slate-600">{c.close_remarks}</p>
            )}
            <p className="text-xs text-slate-400">
              {c.closed_by_name ?? 'Somebody no longer on the system'} on{' '}
              {dateTime(isShort ? c.short_closed_at : c.closed_at)}
            </p>
            {Number(c.retention_still_held) > 0 && (
              <Alert tone="info" title={`${rupees(c.retention_still_held)} retention is still held`}>
                Closing the order does not release the retention — the defect liability period
                outlives the contract. It stays on the statutory register until it goes back.
              </Alert>
            )}
            <p className="flex items-start gap-2 pt-1 text-xs text-slate-500">
              <Lock className="mt-0.5 h-3.5 w-3.5 shrink-0" />
              No new measurement, running account bill or variation can be raised against
              this order.
            </p>

            {/*
              REOPENING — 0164. The guard above has been telling people "the
              order has to be reopened first" since 0156, with no way to do it.
              Only the CMD sees this button.
            */}
            {MAY_REOPEN.includes(profile?.role ?? '') && (
              <button className="btn-secondary btn-sm mt-1" disabled={busy}
                      onClick={() => { setText(''); setAsking('REOPEN') }}>
                <RotateCcw className="h-4 w-4" /> Reopen this order
              </button>
            )}
          </div>
        </div>

        <Modal open={asking === 'REOPEN'} onClose={() => setAsking(null)}
               title="Reopen this work order"
               subtitle="Work has resumed on a contract that was closed."
               footer={
                 <>
                   <button className="btn-secondary" onClick={() => setAsking(null)} disabled={busy}>
                     Cancel
                   </button>
                   <button className="btn-warning" disabled={busy || text.trim().length < 15}
                           onClick={() => void act('REOPEN')}>
                     {busy ? 'Reopening…' : 'Reopen it'}
                   </button>
                 </>
               }>
          <div className="space-y-3">
            <Alert tone="warning" title="This reverses a closure the accounts have already reported">
              The order goes back to live and measurement and billing may resume. Any
              final-bill declaration is withdrawn with it, because &ldquo;final&rdquo; and
              &ldquo;more work is coming&rdquo; cannot both be true.
            </Alert>
            <p className="text-sm text-slate-600">
              Nothing already approved, paid or released is touched. Reopening is not a
              rewind — the closure and this reopening both stay on the record, with their
              reasons.
            </p>
            <Field label="Why the work is resuming" required
                   hint="At least a sentence. It is the only thing that will explain the swing
                         to whoever reads this order next year.">
              <Textarea rows={4} value={text} onChange={(e) => setText(e.target.value)}
                        placeholder="Client reinstated the balance scope on 12-10-2026 under the same rates; letter on file." />
            </Field>
            {text.trim().length > 0 && text.trim().length < 15 && (
              <p className="text-xs text-amber-700">
                A few more words — {15 - text.trim().length} to go.
              </p>
            )}
          </div>
        </Modal>
      </Card>
    )
  }

  /* ------------------------------------------------------------------ open */
  return (
    <>
      <Card title="Closing this order"
            subtitle="An order stays live until somebody ends it. These are the two ways.">
        {blockers.length === 0 ? (
          <Alert tone="success" title="Everything is settled">
            The final bill is approved, every payable is paid and nothing is left in the chain.
            {Number(c.retention_still_held) > 0
              && ` ${rupees(c.retention_still_held)} of retention is still held — that is expected,
                   and it stays on the statutory register after the order closes.`}
          </Alert>
        ) : (
          <div className="space-y-2">
            <p className="flex items-center gap-2 text-sm font-medium text-slate-700">
              <AlertTriangle className="h-4 w-4 text-amber-600" />
              {blockers.length} thing{blockers.length === 1 ? '' : 's'} still outstanding
            </p>
            <ul className="space-y-1.5">
              {blockers.map((b, i) => (
                <li key={i} className="flex items-start gap-2 rounded-lg border border-slate-200
                                       bg-slate-50 px-3 py-2 text-sm text-slate-700">
                  <span className="mt-1.5 h-1.5 w-1.5 shrink-0 rounded-full bg-amber-500" />
                  {b}
                </li>
              ))}
            </ul>

            {/*
              THE DOOR, not another sentence. Drawn only when there is
              something in the chain to open, so a blocker list made entirely
              of payables and retention gets no empty panel.
            */}
            {(openBills.length > 0 || openMbs.length > 0) && (
              <div className="rounded-lg border border-slate-200 bg-white px-3 py-2">
                <p className="text-xs font-medium text-slate-600">
                  Open {openBills.length > 0 && openMbs.length > 0
                    ? 'documents'
                    : openBills.length > 0 ? 'bill' : 'measurement pages'} on this order
                </p>
                <p className="mt-0.5 text-[11px] leading-snug text-slate-400">
                  Each one has to be finished or put down. A page or a bill that is still
                  with its approver is his to return — it cannot be taken out of his queue.
                </p>
                <ul className="mt-2 space-y-1">
                  {(openBills as any[]).map((r) => (
                    <li key={r.id} className="flex flex-wrap items-center gap-x-2 text-xs text-slate-600">
                      <Link to={`/service/ra-bills/${r.id}`}
                            className="inline-flex items-center gap-1 font-medium text-amber-700 hover:underline">
                        RA-{r.ra_no} <ExternalLink className="h-3 w-3" />
                      </Link>
                      <span className="text-slate-400">{String(r.status).toLowerCase()}</span>
                      <span className="text-slate-500">
                        {r.status === 'SUBMITTED'
                          ? '— with its approver'
                          : '— finish it, or withdraw it there'}
                      </span>
                    </li>
                  ))}
                  {(openMbs as any[]).map((r) => (
                    <li key={r.id} className="flex flex-wrap items-center gap-x-2 text-xs text-slate-600">
                      <Link to={`/service/measurements/${r.id}`}
                            className="inline-flex items-center gap-1 font-medium text-amber-700 hover:underline">
                        {r.id} <ExternalLink className="h-3 w-3" />
                      </Link>
                      <span className="text-slate-400">{String(r.status).toLowerCase()}</span>
                      <span className="text-slate-500">
                        {r.status === 'SUBMITTED'
                          ? '— with the Resident Construction Manager'
                          : '— certify it, or withdraw it there'}
                      </span>
                    </li>
                  ))}
                </ul>
              </div>
            )}
          </div>
        )}

        {!mayClose ? (
          <p className="mt-3 text-xs text-slate-500">
            Closing a work order is for Projects, the Procurement Manager or the CMD.
          </p>
        ) : (
          <div className="mt-4 flex flex-wrap gap-2">
            <button className="btn-primary btn-sm" disabled={blockers.length > 0 || busy}
                    onClick={() => { setText(''); setAsking('CLOSE') }}>
              <PackageCheck className="h-4 w-4" /> Close out
            </button>
            <button className="btn-warning btn-sm" disabled={shortBlocked || busy}
                    onClick={() => { setText(''); setAsking('SHORT') }}>
              <CircleSlash className="h-4 w-4" /> Close short
            </button>
            {mayCancel && (
              <button className="btn-danger btn-sm" disabled={busy}
                      onClick={() => { setText(''); setAsking('CANCEL') }}
                      title="For an order raised by mistake, before anything has happened under it">
                <Ban className="h-4 w-4" /> Cancel the order
              </button>
            )}
          </div>
        )}
        {mayClose && blockers.length > 0 && !shortBlocked && (
          <p className="mt-2 text-xs text-slate-500">
            The only thing standing in the way of a normal close is the final bill, so the order
            can still be closed <strong>short</strong> — which is what you do when no final bill
            is coming.
          </p>
        )}
      </Card>

      {/* --------------------------------------------------------------- */}
      <Modal open={asking === 'CLOSE'} onClose={() => setAsking(null)}
             title="Close this work order"
             subtitle="The work is finished and everything is settled."
             footer={
               <>
                 <button className="btn-secondary" onClick={() => setAsking(null)} disabled={busy}>
                   Cancel
                 </button>
                 <button className="btn-primary" disabled={busy} onClick={() => void act('CLOSE')}>
                   {busy ? 'Closing…' : 'Close it out'}
                 </button>
               </>
             }>
        <div className="space-y-3">
          <p className="text-sm text-slate-600">
            Nothing further can be measured, billed or varied against this order afterwards.
            {Number(c.retention_still_held) > 0
              && ` The ${rupees(c.retention_still_held)} of retention stays held and stays on the
                   statutory register — closing the order does not release it.`}
          </p>
          <Field label="Remarks" hint="Optional. A handover date or a reference is useful here.">
            <Textarea rows={3} value={text} onChange={(e) => setText(e.target.value)}
                      placeholder="Handed over 18-09-2026, completion certificate on file." />
          </Field>
        </div>
      </Modal>

      {cancelModal}

      <Modal open={asking === 'SHORT'} onClose={() => setAsking(null)}
             title="Close this order short"
             subtitle="The work has stopped and will not resume."
             footer={
               <>
                 <button className="btn-secondary" onClick={() => setAsking(null)} disabled={busy}>
                   Cancel
                 </button>
                 <button className="btn-warning" disabled={busy || text.trim().length < 10}
                         onClick={() => void act('SHORT')}>
                   {busy ? 'Closing…' : 'Close it short'}
                 </button>
               </>
             }>
        <div className="space-y-3">
          <Alert tone="warning" title="This cannot be undone">
            <span className="flex items-start gap-2">
              <ShieldAlert className="mt-0.5 h-4 w-4 shrink-0" />
              The balance of the contract will not be executed. Money already certified has
              been paid; what has not been done will not be done.
            </span>
          </Alert>
          <Field label="Why the work is stopping" required
                 hint="At least a short sentence. It is kept permanently and it is the only
                       explanation anybody reading this order in five years will have.">
            <Textarea rows={4} value={text} onChange={(e) => setText(e.target.value)}
                      placeholder="Client cancelled the approach road; the alignment moved to the east and the balance was re-tendered under WO-…" />
          </Field>
          {text.trim().length > 0 && text.trim().length < 10 && (
            <p className="text-xs text-amber-700">
              A few more words — {10 - text.trim().length} to go.
            </p>
          )}
        </div>
      </Modal>
    </>
  )
}
